import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ModeType } from '../types';
import type { AppUser } from '@/types/user';
import { DEMO_USERS } from '@/contexts/UsersContext';

interface AppContextType {
  currentUser: AppUser;
  setCurrentUser: (user: AppUser) => void;
  mode: ModeType;
  setMode: (mode: ModeType) => void;
  auditMode: boolean;
  toggleAuditMode: () => void;
  // Global navigation — wired by AppContent via setNavigate()
  handleNavigate: (page: string, data?: { highlightSourceType?: string; accountFilter?: string }) => void;
  setNavigate: (fn: (page: string, data?: { highlightSourceType?: string; accountFilter?: string }) => void) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Default to David Chen (System Administrator) as the initial demo user
const DEFAULT_USER = DEMO_USERS.find(u => u.id === '2') ?? DEMO_USERS[0];

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser>(DEFAULT_USER);
  const [mode, setMode] = useState<ModeType>('live');
  const [auditMode, setAuditMode] = useState<boolean>(false);

  // Global navigation function — AppContent wires this up via setNavigate()
  // so that any component (including the Onboarding Assistant) can navigate
  // without needing to be inside AppContent's scope
  const navigateRef = React.useRef<(page: string, data?: { highlightSourceType?: string; accountFilter?: string }) => void>(
    () => { /* no-op until AppContent wires it up */ }
  );

  const handleNavigate = React.useCallback(
    (page: string, data?: { highlightSourceType?: string; accountFilter?: string }) => {
      navigateRef.current(page, data);
    },
    [],
  );

  const setNavigate = React.useCallback(
    (fn: (page: string, data?: { highlightSourceType?: string; accountFilter?: string }) => void) => {
      navigateRef.current = fn;
    },
    [],
  );

  const toggleAuditMode = () => {
    setAuditMode((prev) => !prev);
  };

  return (
    <AppContext.Provider value={{ currentUser, setCurrentUser, mode, setMode, auditMode, toggleAuditMode, handleNavigate, setNavigate }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
